import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, X, Phone, Mail, User, Trash2, Edit3, Save,
  UserCheck, Target, Bell, ArrowLeft, CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const theme = {
  bg: '#F7F4EF',
  orange: '#E8621A',
  ink: '#1A1713',
  ink3: '#6B6358',
  amber: '#B8760A',
  blue: '#1A4A8A',
  green: '#1D7A4F',
  red: '#C0392B',
};

const AVATAR_COLORS = [
  '#E8621A', '#B8760A', '#1A4A8A', '#1D7A4F', '#7c3aed', 
  '#C0392B', '#2563EB', '#059669', '#D97706', '#4F46E5'
];

const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({
    name: '',
    role: 'setter',
    phone: '',
    email: '',
    avatar_color: AVATAR_COLORS[0]
  });

  // Fetch team members
  const fetchMembers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members`);
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications?unread_only=true`);
      const data = await res.json();
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  // Add member
  const handleAddMember = async () => {
    if (!newMember.name.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMember)
      });
      const data = await res.json();
      if (data.member) {
        setMembers([...members, data.member]);
        setNewMember({ name: '', role: 'setter', phone: '', email: '', avatar_color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)] });
        setShowAddModal(false);
      }
    } catch (err) {
      console.error('Failed to add member:', err);
    }
  };

  // Delete member
  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    
    try {
      await fetch(`${API_URL}/api/team/members/${memberId}`, { method: 'DELETE' });
      setMembers(members.filter(m => m.id !== memberId));
    } catch (err) {
      console.error('Failed to delete member:', err);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, { method: 'PUT' });
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const setters = members.filter(m => m.role === 'setter');
  const closers = members.filter(m => m.role === 'closer');

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/crm" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="font-bebas text-xl" style={{ color: theme.ink }}>ÉQUIPE PRSNL</h1>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold"
              style={{ backgroundColor: theme.orange }}
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Notifications Section */}
        {notifications.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications ({notifications.length})
            </h2>
            <div className="space-y-2">
              {notifications.slice(0, 5).map(notif => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      notif.type === 'status_change' ? 'bg-blue-500' :
                      notif.type === 'assignment' ? 'bg-orange-500' :
                      'bg-green-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{notif.lead_name}</p>
                      <p className="text-xs text-gray-500">{notif.message}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => markNotificationRead(notif.id)}
                    className="text-gray-400 hover:text-green-600"
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Setters Section */}
        <div className="mb-8">
          <h2 className="text-sm font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Setters ({setters.length})
          </h2>
          {setters.length === 0 ? (
            <p className="text-gray-400 text-sm">Aucun setter ajouté</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {setters.map(member => (
                <MemberCard 
                  key={member.id} 
                  member={member} 
                  onDelete={handleDeleteMember}
                />
              ))}
            </div>
          )}
        </div>

        {/* Closers Section */}
        <div>
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Closers ({closers.length})
          </h2>
          {closers.length === 0 ? (
            <p className="text-gray-400 text-sm">Aucun closer ajouté</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {closers.map(member => (
                <MemberCard 
                  key={member.id} 
                  member={member} 
                  onDelete={handleDeleteMember}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Member Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg text-gray-800">Ajouter un membre</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Avatar color selector */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">
                    Couleur
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {AVATAR_COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => setNewMember({ ...newMember, avatar_color: color })}
                        className={`w-8 h-8 rounded-full transition-transform ${
                          newMember.avatar_color === color ? 'ring-2 ring-offset-2 scale-110' : ''
                        }`}
                        style={{ backgroundColor: color, ringColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="Ex: Mathis"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                    Rôle
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewMember({ ...newMember, role: 'setter' })}
                      className={`flex-1 py-3 rounded-xl font-bold text-sm transition-colors ${
                        newMember.role === 'setter' 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      📞 Setter
                    </button>
                    <button
                      onClick={() => setNewMember({ ...newMember, role: 'closer' })}
                      className={`flex-1 py-3 rounded-xl font-bold text-sm transition-colors ${
                        newMember.role === 'closer' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}
                    >
                      🎯 Closer
                    </button>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={newMember.phone}
                    onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                    Email (pour notifications)
                  </label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="mathis@prsnl.fr"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400"
                  />
                </div>

                <button
                  onClick={handleAddMember}
                  disabled={!newMember.name.trim()}
                  className="w-full py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: theme.orange }}
                >
                  Ajouter à l'équipe
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Member Card Component
const MemberCard = ({ member, onDelete }) => {
  const isCloser = member.role === 'closer';
  
  return (
    <motion.div
      layout
      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
          style={{ backgroundColor: member.avatar_color || '#E8621A' }}
        >
          {member.name.charAt(0).toUpperCase()}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-800 truncate">{member.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              isCloser ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {isCloser ? '🎯 Closer' : '📞 Setter'}
            </span>
          </div>
          
          {/* Contact info */}
          <div className="space-y-1">
            {member.phone && (
              <a 
                href={`tel:${member.phone}`}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-600"
              >
                <Phone className="w-3.5 h-3.5" />
                {member.phone}
              </a>
            )}
            {member.email && (
              <a 
                href={`mailto:${member.email}`}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600"
              >
                <Mail className="w-3.5 h-3.5" />
                {member.email}
              </a>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <button
          onClick={() => onDelete(member.id)}
          className="text-gray-400 hover:text-red-500 p-1"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Quick contact buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
        {member.phone && (
          <a
            href={`tel:${member.phone}`}
            className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold bg-green-50 text-green-700 hover:bg-green-100"
          >
            <Phone className="w-3.5 h-3.5" />
            Appeler
          </a>
        )}
        {member.phone && (
          <a
            href={`https://wa.me/${member.phone.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: '#25D366' }}
          >
            💬 WhatsApp
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default TeamPage;
